/**
 * AuthEventsTracker — emits ``user_signup`` / ``user_login`` via the F1.1
 * `useTrackEvent` hook after a real Auth0 redirect callback completes.
 *
 * Card z838xWhL — OBS F1.2 Instrumentar 6 events del wizard + auth.
 *
 * Why a dedicated component
 * -------------------------
 * `Auth0Provider.onRedirectCallback` fires with `appState` but does NOT
 * have `user` available (the SDK only exposes `user` via `useAuth0()`
 * after the token exchange completes). We need `user.sub` to derive
 * `method`, so the tracker sits INSIDE the provider, reads `useAuth0()`,
 * and listens for the ``isAuthenticated`` transition to ``true``.
 *
 * To distinguish a real redirect callback from a silent-auth refresh
 * (the SDK also flips ``isAuthenticated`` to ``true`` at app boot when
 * a refresh token is valid), we gate on the ``inbest.auth.callback_pending``
 * flag written by `onRedirectCallback` — the tracker consumes it once.
 *
 * ``user_signup`` vs ``user_login`` is decided by the presence of the
 * ``inbest.user_seen`` flag in ``localStorage`` (see `authEvents.ts`).
 * Mark-once-then-login on subsequent visits.
 *
 * Zero UI. Always renders null.
 */
import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import { useTrackEvent } from '@/hooks/useTrackEvent';
import {
  consumeAuthCallbackFlag,
  deriveAuthMethod,
  isFirstTimeUser,
  markUserSeen,
} from '@/lib/analytics/authEvents';

export function AuthEventsTracker(): null {
  const { isAuthenticated, user, isLoading } = useAuth0();
  const track = useTrackEvent();

  // Guard-against-double-fire ref. Aunque `consumeAuthCallbackFlag`
  // borra el flag después de leerlo, si el effect re-corre por un
  // cambio de dependencias residual (StrictMode dev), este ref previene
  // que emitamos dos eventos en el mismo mount.
  const emittedRef = useRef(false);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;
    if (emittedRef.current) return;

    const sub = user?.sub;
    if (typeof sub !== 'string' || sub.length === 0) return;

    // Sólo emitimos si un redirect real acaba de completar. Silent-auth
    // al boot NO trae el flag → no-op.
    if (!consumeAuthCallbackFlag()) return;

    emittedRef.current = true;

    const method = deriveAuthMethod(sub);
    const isNew = isFirstTimeUser();
    // Marcar ANTES de emitir para prevenir carreras — si por alguna
    // razón el effect corre dos veces en el mismo redirect, la segunda
    // ya ve ``user_seen`` y emitiría 'user_login' en vez de duplicar
    // 'user_signup'. Como `emittedRef` ya nos protege, este mark también
    // sirve para el próximo login del mismo browser.
    markUserSeen();

    void track(isNew ? 'user_signup' : 'user_login', { method }, { userId: sub });
  }, [isAuthenticated, isLoading, user?.sub, track]);

  return null;
}

export default AuthEventsTracker;
